<template>
  <div>
    <div
      v-if="isLoading"
      class="row col-12 justify-center items-center"
    >
      <q-spinner class="q-mr-md" /> {{ $t('common.loading') }}
    </div>
    <template v-else>
      <div
        v-html="programInstructions"
      />
      <q-stepper
        ref="formStepper"
        :model-value="currentProgramFormItem"
        contracted
        flat
        color="primary"
        animated
        :header-class="stepperHeaderClass"
      >
        <q-step
          v-for="programFormItem in programFormItems"
          :key="programFormItem.id"
          :name="programFormItem.id"
          :title="programFormItem.label"
          icon="circle"
          inactive-icon="circle"
          :active-icon="activeIcon"
          active-color="primary"
          done-icon="done"
          done-color="green"
          error-icon="error"
          error-color="negative"
          :done="isStepDone(programFormItem)"
          :error="isStepFailed(programFormItem)"
        >
          <p class="text-bold">
            {{ programFormItem.label }}
          </p>
          <div
            v-if="programFormItem.formInputType === 'asrText'"
            class="row q-col-gutter-md col-12 q-pa-xs "
          >
            <div class="row col-12">
              <transition
                appear
                enter-active-class="animated zoomIn slower delay-1s"
              >
                <q-input
                  :model-value="inputTextResponses[programFormItem.id]"
                  outlined
                  dense
                  autogrow
                  class="col-12"
                  @update:model-value="onInputFormText(programFormItem, $event)"
                />
              </transition>
            </div>
            <div
              class="row col-12"
            >
              <transition
                appear
                enter-active-class="animated zoomIn slower delay-1s"
              >
                <q-btn
                  :label="$t('common.buttonContinue')"
                  :disable="!validateStep(programFormItem)"
                  color="primary-variant"
                  class="col-12 animation-delay-1"
                  data-e2e="button-continue"
                  @click="onContinueForm(programFormItem)"
                />
              </transition>
            </div>
          </div>
          <div
            v-else-if="programFormItem.formInputType === 'asrOptions'"
            class="row q-col-gutter-md col-12 q-pa-xs "
          >
            <div
              v-for="(option, optionIndex) in programFormItem.options"
              :key="option.value"
              class="row col-12"
            >
              <transition
                appear
                enter-active-class="animated zoomIn slower delay-2s"
              >
                <q-btn
                  :label="option.label"
                  color="primary-variant"
                  no-caps
                  class="col-12"
                  :class="'animation-delay-' + optionIndex"
                  @click="onSelectFormOption(programFormItem, option)"
                />
              </transition>
            </div>
          </div>
          <q-form
            v-else-if="programFormItem.formInputType === 'asrList'"
            class="row q-col-gutter-md col-12 q-pa-xs "
            @submit.prevent
          >
            <div
              v-for="(item, itemIndex) in parseInt(programFormItem.maxItems, 10)"
              :key="itemIndex"
              class="row col-12"
            >
              <transition
                appear
                enter-active-class="animated zoomIn slower delay-1s"
              >
                <q-input
                  :model-value="inputItemsResponses[programFormItem.id][itemIndex]"
                  outlined
                  dense
                  hide-bottom-space
                  :rules="[val => ((itemIndex >= programFormItem.minItems) || !!val)
                    || $t('programs.programForm.validateTooFewFormItems',
                          { minItems: programFormItem.minItems })
                  ]"
                  lazy-rules
                  class="col-12"
                  :class="'animation-delay-' + itemIndex"
                  @update:model-value="onInputFormListItem(programFormItem, itemIndex, $event)"
                />
              </transition>
            </div>
            <div
              class="row col-12"
            >
              <transition
                appear
                enter-active-class="animated zoomIn slower delay-1s"
              >
                <q-btn
                  :label="$t('common.buttonContinue')"
                  :disable="!validateStep(programFormItem)"
                  color="primary-variant"
                  class="col-12"
                  :class="'animation-delay-' + (parseInt(programFormItem.maxItems)+1)"
                  data-e2e="button-continue"
                  @click="onContinueForm(programFormItem)"
                />
              </transition>
            </div>
          </q-form>
          <div
            v-if="programFormItem.formInputType === 'asrFileUpload'"
            class="row q-col-gutter-md col-12 q-pa-xs "
          >
            <div class="row col-12">
              <transition
                appear
                enter-active-class="animated zoomIn slower delay-1s"
              >
                <file-attachment
                  v-if="activeSession"
                  ref="attachment"
                  :session="activeSession"
                  :session-program="currentSessionProgramId"
                  :label="$t('programs.programForm.btnUploadVideoLabel')"
                  icon="videocam"
                  size="md"
                  color="primary"
                  accept="video/mp4,video/*"
                  :file-path="'/sessions/' + activeSession.id + '/'"
                  full-width
                  @select="onAddFile"
                  @upload-complete="onUploadComplete"
                >
                  <q-badge
                    v-if="videosUploaded"
                    color="positive"
                    floating
                  >
                    {{ videosUploaded }}
                  </q-badge>
                </file-attachment>
              </transition>
            </div>
            <div
              class="row col-12"
            >
              <transition
                appear
                enter-active-class="animated zoomIn slower delay-1s"
              >
                <template
                  v-if="hasContinueButton(programFormItem)"
                >
                  <q-btn
                    :label="$t('common.buttonContinue')"
                    :disable="!validateStep(programFormItem)"
                    color="primary-variant"
                    class="col-12 animation-delay-1"
                    data-e2e="button-continue"
                    @click="onContinueForm(programFormItem)"
                  />
                </template>
                <template
                  v-else-if="validateStep(programFormItem)"
                >
                  <automation-action
                    :automation-action="successAction"
                    @done="onCompleteAutomationAction"
                  />
                </template>
              </transition>
              <transition
                v-for="otherAction in otherActions"
                :key="otherAction.id"
                appear
                enter-active-class="animated zoomIn slower delay-1s"
              >
                <automation-action
                  :automation-action="otherAction"
                  flat
                  color="dark-variant"
                  @done="onCompleteAutomationAction"
                />
              </transition>
            </div>
          </div>
        </q-step>
        <q-step
          v-if="hasResultsPage"
          name="results"
          title="Results"
          icon="grading"
          active-icon="grading"
        >
          <transition
            appear
            enter-active-class="animated zoomIn"
          >
            <div class="q-mb-lg text-center text-h5">
              {{ $t('programs.programForm.textResults', { numberOfCorrect, numberOfFormItems }) }}
            </div>
          </transition>
          <div
            v-if="allCorrect"
            class="text-center"
          >
            <p>
              {{ $t('programs.programForm.textPositiveResultMessage') }}
            </p>
            <automation-action
              :automation-action="successAction"
              @done="onCompleteAutomationAction"
            />
          </div>
          <div
            v-else
            class="text-center"
          >
            <p>
              {{ $t('programs.programForm.textNegativeResultMessage') }}
            </p>
            <automation-action
              :automation-action="failAction"
              @done="onFailedButtonDone"
            />
          </div>
        </q-step>
      </q-stepper>
    </template>
  </div>
</template>
<script>
import FileAttachment from 'components/FileAttachment';
import AutomationAction from 'components/Programs/Automation/AutomationAction';
import { chain, find, findIndex, get, isEmpty, reduce, size } from 'lodash';
import { mapState } from 'pinia';
import ProgramAutomationActionsDB from 'src/fb/programAutomationActions-db';
import ProgramFormItemsDB from 'src/fb/programFormItems-db';
import { promptLevels } from 'src/store/promptLevels';
import { sessions } from 'src/store/sessions';

export default {

  name: 'ProgramForm',

  components: { AutomationAction, FileAttachment },

  props: {
    sessionProgram: {
      type: Object,
      required: true,
    },

    data: {
      type: Object,
      required: true,
    },

  },

  emits: ['automation-action-completed', 'record', 'record-program-data'],

  data() {
    return {
      isLoading: false,

      programFormItems: [],

      responses: [],

      successAction: {},
      failAction: {},
      otherActions: [],

      filesUploaded: [],
    };
  },

  computed: {
    ...mapState(sessions, ['activeSession', 'activeSessionPrograms']),
    ...mapState(promptLevels, ['activePromptLevels']),

    currentSessionProgramId() {
      return chain(this.activeSessionPrograms)
        .find({ id: get(this.activeSession, 'currentProgram') })
        .get('id', null)
        .value();
    }, // currentSessionProgram()

    hasResultsPage() {
      return this.formType === 'asr';
    }, // hasResultsPage()

    stepperHeaderClass() {
      let steps = this.numberOfFormItems;

      if (this.hasResultsPage) {
        steps += 1;
      }
      return (steps > 1) ? '' : 'hidden';
    }, // stepperHeaderClass()

    programInstructions() {
      return get(this.sessionProgram, ['program', 'instructions'], '');
    }, // programInstructions()

    programPromptLevelTemplate() {
      return find(this.activePromptLevels, { id: get(this.sessionProgram, ['program', 'promptLevelTemplate']) });
    }, // programPromptLevelTemplate()

    programPrompts() {
      return get(this.programPromptLevelTemplate, 'promptLevels');
    }, // programPrompts()

    targets() {
      return get(this.sessionProgram, ['program', 'targets']);
    }, // targets()

    currentTrial() {
      return get(this.data, 'currentTrial', 1);
    }, // currentTrial()

    currentProgramFormItem() {
      return get(this.data, 'currentProgramFormItem') || chain(this.programFormItems).head().get('id').value();
    }, // currentProgramFormItem()

    numberOfCorrect() {
      return reduce(this.programFormItems, (pV, programFormItem) => {
        return pV + (this.isStepFailed(programFormItem) ? 0 : 1);
      }, 0);
    }, // numberOfCorrect()

    numberOfFormItems() {
      return size(this.programFormItems);
    }, // numberOfCorrect()

    allCorrect() {
      return this.numberOfCorrect === this.numberOfFormItems;
    }, // allCorrect()

    formType() {
      return get(this.sessionProgram, ['program', 'formType'], 'asr');
    }, // formType()

    activeIcon() {
      return this.formType === 'asr' ? 'help' : 'edit';
    }, // activeIcon()

    inputTextResponses() {
      const { responses } = this;

      return chain(this.programFormItems)
        .keyBy('id')
        .mapValues((programFormItem) => {
          return get(responses, programFormItem.id) || '';
        })
        .value();
    }, // inputTextResponses()

    inputItemsResponses() {
      const { responses } = this;

      return chain(this.programFormItems)
        .keyBy('id')
        .mapValues((programFormItem) => {
          return get(responses, programFormItem.id) || [];
        })
        .value();
    }, // inputItemsResponses()

    responsesText() {
      return chain(this.programFormItems)
        .map((programFormItem) => {
          const programFormItemId = get(programFormItem, 'id');
          let retVal = `${programFormItem.label}\n`;

          switch (programFormItem.formInputType) {
            case 'asrText':
              retVal += get(this.responses, programFormItemId, '');
              break;
            case 'asrOptions':
              retVal += chain(programFormItem)
                .get('options')
                .find({ value: get(this.responses, programFormItemId) })
                .get('label', '')
                .value();

              if (this.isStepFailed(programFormItem)) {
                retVal = `${retVal} [${this.$t('programs.programForm.incorrectOption')}]`;
              } else {
                retVal = `${retVal} [${this.$t('programs.programForm.correctOption')}]`;
              }
              break;
            case 'asrList':
              retVal += chain(this.responses)
                .get(programFormItemId, '')
                .map((item) => `* ${item}`)
                .join('\n')
                .value();
              break;
            default:
              return '';
          }
          return retVal;
        })
        .join('\n\n---\n')
        .value();
    }, // responsesText()

    videosUploaded() {
      return reduce(
        this.filesUploaded,
        (pV, file) => (
          pV + (chain(file).get('type').startsWith('video').value() ? 1 : 0)),
        0,
      );
    }, // videosUploaded()

  },

  watch: {

    'sessionProgram.program.id': {
      immediate: true,
      async handler(newProgramId) {
        this.isLoading = true;
        const programFormItemsDb = new ProgramFormItemsDB(newProgramId);
        this.programFormItems = await programFormItemsDb.readAll(null, 'order');

        await this.reloadProgramAutomationActions(this.sessionProgram.program);

        this.responses = {};
        this.isLoading = false;
      },
    },

    currentProgramFormItem: {
      immediate: false,
      async handler(newProgramFormItemId) {
        if (newProgramFormItemId && !('newProgramFormItemId' in this.responses)) {
          this.responses[newProgramFormItemId] = null;
        }
      },
    },
  },

  methods: {

    onSelectFormOption(currentProgramFormItem, selectedOption) {
      this.responses[currentProgramFormItem.id] = selectedOption.value;

      const promptLevel = find(this.programPrompts, { id: get(selectedOption, 'promptLevel') });
      const data = {
        promptLevel: get(promptLevel, 'id', null),
        scoreValue: get(promptLevel, 'score', null),
      };

      this.onRecordData(currentProgramFormItem, data, true);
    }, // onSelectOption()

    onInputFormListItem(currentProgramFormItem, itemIndex, value) {
      if (!this.responses[currentProgramFormItem.id]) {
        this.responses[currentProgramFormItem.id] = [];
      }
      this.responses[currentProgramFormItem.id][itemIndex] = value;
    }, // onInputFormListItem()

    onInputFormText(currentProgramFormItem, value) {
      this.responses[currentProgramFormItem.id] = value;
    }, // onInputFormText()

    onAddFile(file) {
      this.addFile(file);
    }, // onAddFile()

    async onUploadComplete(uploadedFileInfo) {
      this.addFile(uploadedFileInfo);
    }, // onUploadComplete()

    onRecordData(currentProgramFormItem, data, moveForward = false) {
      const target = find(this.targets, { id: get(currentProgramFormItem, 'target', null) });
      const saveData = { ...data, trial: this.currentTrial };

      if (moveForward) {
        this.moveToNextFormItem(currentProgramFormItem);
      }

      if (target) {
        this.$emit('record', this.sessionProgram, target, saveData);
      }
    }, // onRecordData()

    onContinueForm(currentProgramFormItem) {
      const notes = this.responsesText || null;
      this.moveToNextFormItem(currentProgramFormItem, notes);
    }, // onContinueForm()

    onCompleteAutomationAction(automationActionResults) {
      this.$emit('automation-action-completed', automationActionResults);
    }, // onCompleteAutomationAction()

    onFailedButtonDone(automationActionResults) {
      this.isLoading = true;
      this.$emit('record-program-data', this.sessionProgram, {
        currentProgramFormItem: null,
        currentTrial: this.currentTrial + 1,
      });
      this.$nextTick(() => {
        this.onCompleteAutomationAction(automationActionResults);
      });
    }, // onFailedButtonDone()

    isStepDone(programFormItem) {
      switch (programFormItem.formInputType) {
        case 'asrText':
        case 'asrList':
          return !chain(this.responses).get(programFormItem.id, '').isEmpty().value();
        default:
      }
      return chain(this.data)
        .get('data')
        .find({
          trial: this.currentTrial,
          target: get(programFormItem, 'target'),
        })
        .has('scoreValue')
        .value();
    }, // isStepDone()

    isStepFailed(programFormItem) {
      if (!this.isStepDone(programFormItem)) {
        return false;
      }
      return !this.validateStep(programFormItem);
    }, // isStepFailed()

    validateStep(programFormItem) {
      switch (programFormItem.formInputType) {
        case 'asrOptions':
          return chain(this.data)
            .get('data')
            .find({
              trial: this.currentTrial,
              target: get(programFormItem, 'target'),
            })
            .get('scoreValue', 0)
            .value() > 0;
        case 'asrList':
          return chain(this.responses)
            .get(get(programFormItem, 'id'))
            .size()
            .value()
            >= parseInt(get(programFormItem, 'minItems'), 10);
        case 'asrFileUpload':
          return this.videosUploaded > 0;
        default:
          return true;
      }
    }, // validateStep()

    /**
     * Add (or replace) files to uploaded array
     */
    addFile(file) {
      const existingFileIndex = findIndex(
        this.filesUploaded,
        { originalFilename: file.originalFilename },
      );
      if (existingFileIndex !== -1) {
        this.filesUploaded.splice(existingFileIndex, 1, file);
      } else {
        this.filesUploaded.push(file);
      }
    }, // addFile()

    programFormItemIcon(programFormItem) {
      const response = get(this.responses, get(programFormItem, 'id'), null);
      if (response === null) {
        return 'circle';
      }
      return (response === get(programFormItem, 'correctOption')) ? 'correct' : 'incorrect';
    }, // programFormItemIcon()

    hasContinueButton(currentProgramFormItem) {
      return this.hasResultsPage
        && (this.getNextFormItem(currentProgramFormItem) === 'results');
    }, // hasContinueButton()

    getNextFormItem(currentProgramFormItem) {
      const currentProgramFormItemIndex = chain(this.programFormItems)
        .findIndex({ id: get(currentProgramFormItem, 'id') })
        .defaultTo(-1)
        .value();
      let nextProgramFormItemId = 'results';
      if ((currentProgramFormItemIndex + 1) < size(this.programFormItems)) {
        nextProgramFormItemId = get(this.programFormItems, [currentProgramFormItemIndex + 1, 'id']);
      }
      return nextProgramFormItemId;
    }, // getNextFormItem()

    async moveToNextFormItem(currentProgramFormItem, notes = null) {
      const nextProgramFormItemId = this.getNextFormItem(currentProgramFormItem);

      this.$emit('record-program-data', this.sessionProgram, {
        currentProgramFormItem: nextProgramFormItemId,
        notes,
      });
    }, // moveToNextFormItem()

    async reloadProgramAutomationActions(program) {
      if (!program || isEmpty(program)) {
        return;
      }
      try {
        const programAutomationActionsDb = new ProgramAutomationActionsDB(program.id);
        const programAutomationActions = await programAutomationActionsDb.readAll();
        this.successAction = chain(programAutomationActions)
          .filter({ buttonType: 'quizSuccess' })
          .head()
          .defaultTo({})
          .value();
        this.failAction = chain(programAutomationActions)
          .filter({ buttonType: 'quizFail' })
          .head()
          .defaultTo({})
          .value();

        this.otherActions = chain(programAutomationActions)
          .reject({ buttonType: 'quizSuccess' })
          .reject({ buttonType: 'quizFail' })
          .value();
      } catch {
        this.successAction = {};
        this.failAction = {};
      }
    }, // reloadProgramAutomationActions

  },
};
</script>
<style lang="scss" scoped>

@for $i from 0 through 9 {
  .animation-delay-#{$i} {
    animation-delay: #{100*($i+3)}ms;
  }
}
</style>
